import { clsx, type ClassValue } from "clsx"
import { extendTailwindMerge } from "tailwind-merge"

/**
 * Custom `text-*` sizes have to be declared here or tailwind-merge cannot tell
 * them from `text-*` colours: both match the same prefix, so it treats them as
 * one conflicting group and keeps only the last. A size and a colour on the same
 * element then silently loses the size — no error, no warning, the element just
 * renders at the inherited 16px.
 *
 * Every key added to `v2FontSize` or the apps' `theme.fontSize` belongs in this
 * list.
 */
/**
 * Every radius token the v2 scale ships. Mirrors `v2BorderRadius`.
 */
const V2_RADII = ["v2-sm", "v2-md", "v2-lg", "v2-xl", "v2-2xl", "v2-3xl"]

/**
 * The radius group has the same blind spot as `font-size`: without the custom
 * tokens declared, `rounded-v2-lg` and a stock `rounded-none` look unrelated,
 * so both survive the merge and stylesheet order decides which wins instead of
 * the caller's override. Corner-specific utilities are separate groups, and
 * several are in real use, so each one gets the tokens too.
 */
const V2_ROUNDED_GROUP_IDS = [
  "rounded",
  "rounded-s", "rounded-e", "rounded-t", "rounded-r", "rounded-b", "rounded-l",
  "rounded-ss", "rounded-se", "rounded-ee", "rounded-es",
  "rounded-tl", "rounded-tr", "rounded-br", "rounded-bl",
] as const

const v2RoundedClassGroups = Object.fromEntries(
  V2_ROUNDED_GROUP_IDS.map((id) => [id, [{ [id]: V2_RADII }]]),
)

const twMerge = extendTailwindMerge({
  extend: {
    classGroups: {
      'font-size': [
        {
          text: [
            'headline',
            'kpi',
            'eyebrow',
            'caption',
            'micro',
            'v2-eyebrow',
            'v2-xs',
            'v2-sm',
            'v2-base',
            'v2-lg',
          ],
        },
      ],
      /**
       * Same problem in the radius group. Without these, `rounded-v2-lg` and a
       * stock `rounded-none` are treated as unrelated and both survive the
       * merge, so a caller overriding a component's radius gets two competing
       * declarations settled by stylesheet order rather than by the override.
       *
       * Every key in `v2BorderRadius` belongs in this list.
       */
      ...v2RoundedClassGroups,
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}